/**
 * Section Utilisateurs (personnel) —
 * GET /api/admin/users : une page de comptes, les compteurs des onglets et
 * les chiffres du haut de page ;
 * GET /api/admin/users/:id/summary : la fiche courte du panneau latéral.
 */

export type AdminUsersTab = 'all' | 'users' | 'staff' | 'pending' | 'suspended' | 'deleted';
export type AdminUsersSort = 'recent' | 'oldest' | 'name' | 'last_login' | 'trips' | 'listings';
export type UserVerification = 'verified' | 'pending' | 'none';

export interface AdminUserRow {
  id_user: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  profile_photo: string | null;
  role: 'user' | 'moderator' | 'admin' | string;
  is_suspended: boolean;
  is_checked: boolean;
  auth_provider: string;
  registration_date: string | null;
  last_login: string | null;
  average_rating: number | string | null;
  total_reviews: number | null;
  lang: string | null;
  /** Dernier téléphone vu par l'API (X-App-Platform / X-App-Version), et quand. */
  last_platform?: 'android' | 'ios' | null;
  last_app_version?: string | null;
  last_seen_at?: string | null;
  listings: number;
  trips: number;
  verification: UserVerification;
  email_verified_at?: string | null;
}

/** Compte supprimé, tel que le journal d'audit l'a gardé. */
export interface DeletedUserRow {
  id_user: string;
  email: string | null;
  deleted_at: string;
  deleted_by_name: string | null;
}

export interface AdminUsersPage {
  counts: Record<AdminUsersTab, number | null>;
  kpis: {
    accounts: number;
    new_7d: number;
    new_prev_7d: number;
    new_change_pct: number | null;
    pending: number;
    pending_today: number;
    staff: number;
    admins: number;
    moderators: number;
  };
  tab: AdminUsersTab;
  page: number;
  limit: number;
  total: number;
  rows: Array<AdminUserRow | DeletedUserRow>;
}

export interface AdminUsersQuery {
  q?: string;
  tab?: AdminUsersTab;
  role?: string;
  status?: string;
  verification?: string;
  source?: string;
  sort?: AdminUsersSort;
  page?: number;
  limit?: number;
}

export interface UserDocument {
  id_verification: string;
  type: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  created_at: string | null;
  updated_at: string | null;
  verification_date: string | null;
}

export type UserActivityKind = 'listing_created' | 'rental_requested' | 'rental_completed' | 'review_written' | 'document_sent' | 'registered';

export interface UserActivity {
  kind: UserActivityKind;
  at: string;
  label: string | null;
  ref: string | null;
}

export interface AdminUserSummary {
  user: AdminUserRow;
  documents: UserDocument[];
  activity: UserActivity[];
}

const call = async <T,>(path: string, token: string, signal?: AbortSignal): Promise<T> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` }, signal });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data.data as T;
};

export const getAdminUsers = (token: string, query: AdminUsersQuery, signal?: AbortSignal) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '' && value !== 'all') params.set(key, String(value));
  });
  return call<AdminUsersPage>(`api/admin/users?${params.toString()}`, token, signal);
};

export const getAdminUserSummary = (token: string, id: string, signal?: AbortSignal) =>
  call<AdminUserSummary>(`api/admin/users/${encodeURIComponent(id)}/summary`, token, signal);
